import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Bell, Check, CaretRight } from "@phosphor-icons/react"
import { Button } from "../ui/button"
import { Avatar, AvatarFallback } from "../ui/avatar"
import {
  useNotificationCount,
  useNotifications,
  useMarkAllNotificationsRead,
  Notification,
} from "../../hooks"
import { cn, formatRelativeTime } from "../../lib/utils"

function NotificationItem({
  notification,
  onClose,
}: {
  notification: Notification
  onClose: () => void
}) {
  const navigate = useNavigate()

  const handleClick = () => {
    if (notification.action_url) {
      navigate(notification.action_url)
    } else if (notification.matter) {
      navigate(`/matters/${notification.matter}`)
    }
    onClose()
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        "flex items-start gap-3 w-full px-4 py-3 text-left",
        "transition-colors duration-100",
        "hover:bg-muted",
        !notification.read_at && "bg-primary/5"
      )}
    >
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className="text-xs bg-muted text-muted-foreground">
          {(notification.icon || notification.category || "N")[0].toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground font-medium">
          {notification.category.replace(/_/g, " ")}
        </p>
        <p className="text-sm text-foreground line-clamp-2">{notification.body}</p>
        <p className="text-[10px] text-muted-foreground mt-1">
          {formatRelativeTime(notification.created_at)}
        </p>
      </div>
      {!notification.read_at && (
        <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-2" />
      )}
    </button>
  )
}

export function NotificationButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [badgeAnimating, setBadgeAnimating] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const prevCountRef = useRef<number>(0)

  const { data: count = 0 } = useNotificationCount()
  const { data: notifications = [] } = useNotifications(5)
  const markAllRead = useMarkAllNotificationsRead()

  // Animate badge when count increases
  useEffect(() => {
    if (count > prevCountRef.current && prevCountRef.current > 0) {
      setBadgeAnimating(true)
      setTimeout(() => setBadgeAnimating(false), 300)
    }
    prevCountRef.current = count
  }, [count])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const handleMarkAllRead = () => {
    markAllRead.mutate()
  }

  const handleViewAll = () => {
    setIsOpen(false)
    navigate("/notifications/unread")
  }

  // Split notifications into new (unread) and earlier (read)
  const newNotifications = notifications.filter((n: Notification) => !n.read_at)
  const earlierNotifications = notifications.filter((n: Notification) => n.read_at)

  return (
    <div ref={menuRef} className="relative">
      <Button
        variant="ghost"
        size="icon-sm"
        className="relative group"
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <Bell
          className={cn(
            "h-4 w-4 transition-colors",
            isOpen ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"
          )}
        />
        {count > 0 && (
          <span
            className={cn(
              "absolute -top-0.5 -right-0.5",
              "flex h-[18px] min-w-[18px] items-center justify-center",
              "rounded-full px-1",
              "text-[10px] font-bold text-white",
              "bg-gradient-to-br from-primary to-primary-600",
              "shadow-sm shadow-primary/25",
              "transition-transform duration-200",
              "group-hover:scale-110",
              badgeAnimating && "animate-notification-pop"
            )}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Button>

      {isOpen && (
        <div
          className={cn(
            "absolute right-0 top-full mt-2 rounded-xl",
            "w-[calc(100vw-2rem)] sm:w-80",
            "bg-background border border-border shadow-xl",
            "overflow-hidden z-50",
            "animate-flyout-enter"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
            {count > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary-600 transition-colors"
                disabled={markAllRead.isPending}
              >
                <Check className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <Bell className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No notifications</p>
              </div>
            ) : (
              <>
                {/* New (unread) notifications */}
                {newNotifications.length > 0 && (
                  <div>
                    <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted">
                      New
                    </div>
                    {newNotifications.map((notification: Notification) => (
                      <NotificationItem
                        key={notification._id}
                        notification={notification}
                        onClose={() => setIsOpen(false)}
                      />
                    ))}
                  </div>
                )}

                {/* Earlier (read) notifications */}
                {earlierNotifications.length > 0 && (
                  <div>
                    <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted">
                      Earlier
                    </div>
                    {earlierNotifications.map((notification: Notification) => (
                      <NotificationItem
                        key={notification._id}
                        notification={notification}
                        onClose={() => setIsOpen(false)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <button
            onClick={handleViewAll}
            className={cn(
              "flex items-center justify-center gap-1 w-full px-4 py-3",
              "text-sm text-primary font-medium",
              "border-t border-border",
              "hover:bg-muted transition-colors"
            )}
          >
            View all notifications
            <CaretRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
